<template>
  <div class="supplier-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-truck-delivery-outline</v-icon>
          </div>
          <div>
            <div class="page-title">SUPPLIER MANAGEMENT</div>
            <div class="page-subtitle">Manage vendor directory, bullion sourcing &amp; gold ledger balances</div>
          </div>
        </div>

        <!-- Right: Action Bar (Search + Refresh + Add Supplier) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search by name, code, mobile, location..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            icon
            small
            title="Refresh List"
            class="refresh-icon-btn"
            @click="getSupplier()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="openAddSupplier()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add Supplier
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">REGISTERED SUPPLIERS</div>
          <div class="kpi-value">{{ totalSuppliersCount }}</div>
          <div class="kpi-hint">Active vendors in directory</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-domain</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL PURCHASED GOLD</div>
          <div class="kpi-value">{{ totalPurchasedGoldSum }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Cumulative net gold weight</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">BALANCE THANKAM</div>
          <div class="kpi-value">{{ totalBalanceThankamSum }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Unsettled bullion balance</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-scale-balance</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">UNPAID GOLD WEIGHT</div>
          <div class="kpi-value">{{ totalPendingGoldSum }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Pending settlement gold</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fff7ed; color: #ea580c;">
          <v-icon size="20" color="#ea580c">mdi-clock-alert-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table / Content Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Data Table View -->
      <div v-if="supplierList && supplierList.length > 0">
        <v-simple-table class="pro-master-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 55px;">S.No.</th>
                <th class="text-left" style="min-width: 180px;">Supplier Details</th>
                <th class="text-left" style="width: 120px;">Code</th>
                <th class="text-left" style="width: 150px;">Contact Number</th>
                <th class="text-right" style="width: 160px;">Purchased Gold (gm)</th>
                <th class="text-right" style="width: 160px;">Balance Thankam (gm)</th>
                <th class="text-right" style="width: 150px;">Unpaid Gold (gm)</th>
                <th class="text-center" style="width: 130px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, i) in supplierList"
                :key="i"
                class="master-table-row"
              >
                <td class="text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>
                <td>
                  <div class="d-flex align-center">
                    <div class="supplier-avatar mr-2">
                      {{ getInitials(value.supplierName) }}
                    </div>
                    <div>
                      <div class="supplier-name-text">
                        {{ value.supplierName || '—' }}
                      </div>
                      <div class="supplier-location-text" v-if="value.supplierLocation">
                        <v-icon x-small color="#64748b" class="mr-1">mdi-map-marker-outline</v-icon>
                        {{ value.supplierLocation }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="code-pill">
                    {{ value.supplierCode || '—' }}
                  </span>
                </td>
                <td>
                  <div class="contact-text font-weight-medium">
                    {{ value.contactNumber || '—' }}
                  </div>
                  <div class="text-caption text-muted" v-if="value.email">
                    {{ value.email }}
                  </div>
                </td>
                <td class="text-right font-weight-semibold" style="color: #0f172a;">
                  {{ formatWeight(value.totalPurchasedGold) }}
                </td>
                <td class="text-right font-weight-semibold" style="color: #0284c7;">
                  {{ formatWeight(value.balanceThankam) }}
                </td>
                <td class="text-right font-weight-semibold" :style="{ color: value.totalPendingGold > 0 ? '#b91c1c' : '#475569' }">
                  {{ formatWeight(value.totalPendingGold) }}
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center" style="gap: 4px;">
                    <v-btn
                      icon
                      x-small
                      title="View Supplier Ledger &amp; Details"
                      class="action-icon-btn"
                      @click="$router.push('/SupplierDetail?id=' + value._id)"
                    >
                      <v-icon size="16" color="#0284c7">mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Edit Supplier"
                      class="action-icon-btn"
                      @click="openEditSupplier(value)"
                    >
                      <v-icon size="16" color="#475569">mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Delete Supplier"
                      class="action-icon-btn delete-btn"
                      @click="openDeleteSupplier(value._id)"
                    >
                      <v-icon size="16" color="#dc2626">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Pagination Controls -->
        <div class="d-flex align-center justify-space-between px-4 py-3 border-t">
          <span class="text-caption text-muted">
            Showing {{ supplierList.length }} suppliers (Page {{ page }} of {{ Pagelength || 1 }})
          </span>
          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            total-visible="7"
            color="#9a6b27"
            class="pro-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Professional Empty State -->
      <div v-else class="empty-state-wrap py-12 text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="36" color="#94a3b8">mdi-truck-cargo-container</v-icon>
        </div>
        <div class="empty-title">No suppliers found</div>
        <div class="empty-subtitle">
          No vendor records match your search or no suppliers have been registered yet.
        </div>
        <div class="mt-4">
          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize"
            @click="openAddSupplier()"
          >
            <v-icon left small>mdi-plus</v-icon>
            Add First Supplier
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Modal 1: Add Supplier Dialog -->
    <v-dialog max-width="800px" v-model="addSupplier" scrollable persistent content-class="admin-supplier-dialog">
      <v-card class="modal-card rounded-lg overflow-hidden d-flex flex-column" style="max-height: 85vh;">
        <div class="modal-header d-flex align-center justify-space-between px-6 py-4" style="flex-shrink: 0;">
          <div class="d-flex align-center">
            <v-icon color="#9a6b27" class="mr-2">mdi-account-plus-outline</v-icon>
            <div>
              <div class="modal-title">Register New Supplier</div>
              <div class="modal-subtitle">Add vendor profile, statutory credentials &amp; bank settlement details</div>
            </div>
          </div>
          <v-btn icon small @click="closeAddSupplier">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form v-model="addcat" ref="addcat" @submit.prevent class="d-flex flex-column overflow-hidden flex-grow-1">
          <div class="modal-body px-6 py-4 overflow-y-auto flex-grow-1" style="max-height: calc(85vh - 130px);">
            <!-- Section 1: Basic Information -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-card-account-details-outline</v-icon>
              <span>1. General Supplier Information</span>
            </div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <label class="field-label">Supplier Name <span class="required-star">*</span></label>
                <v-text-field
                  :rules="[rules.required]"
                  placeholder="e.g. Royal Bullion Traders"
                  v-model="supplierName"
                  outlined
                  dense
                  color="#9a6b27"
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Mobile Number</label>
                <v-text-field
                  placeholder="e.g. 9876543210"
                  v-model="contact"
                  outlined
                  color="#9a6b27"
                  dense
                  type="number"
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Email Address</label>
                <v-text-field
                  color="#9a6b27"
                  type="email"
                  placeholder="vendor@company.com"
                  v-model="email"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">City / Location</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="e.g. Thrissur"
                  v-model="location"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">PAN Card No</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="e.g. ABCDE1234F"
                  v-model="pan"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Supplier Full Address</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Building, street, pincode"
                  v-model="address"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4" style="border-color: #f1f5f9;"></v-divider>

            <!-- Section 2: Statutory & Tax Credentials -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-shield-check-outline</v-icon>
              <span>2. Statutory &amp; Regulatory Credentials</span>
            </div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <label class="field-label">GSTIN</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="e.g. 32AAAAA0000A1Z5"
                  v-model="gstin"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">TAN Number</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Tax Deduction Acc No"
                  v-model="tan"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">BIS Hallmark License</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="License identification"
                  v-model="bisliscence"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Insurance Policy No</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Transit / Bullion policy"
                  v-model="policyno"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Company ID / CIN</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Corporate registry ID"
                  v-model="companyid"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4" style="border-color: #f1f5f9;"></v-divider>

            <!-- Section 3: Banking & Payment Settlement -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-bank-outline</v-icon>
              <span>3. Banking &amp; Settlement Account</span>
            </div>
            <v-row dense>
              <v-col cols="12" sm="3">
                <label class="field-label">Bank Name</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="e.g. State Bank of India"
                  v-model="bankname"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <label class="field-label">Account Number</label>
                <v-text-field
                  color="#9a6b27"
                  type="number"
                  placeholder="Account number"
                  v-model="accno"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <label class="field-label">IFSC Code</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="e.g. SBIN0001234"
                  v-model="ifsc"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <label class="field-label">Bank Branch</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Branch location"
                  v-model="branch"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <div class="modal-footer d-flex align-center justify-end px-6 py-3 border-t bg-white" style="flex-shrink: 0; background: #ffffff; border-top: 1px solid #e2e8f0;">
            <v-btn
              text
              class="btn-cancel text-capitalize mr-2"
              @click="closeAddSupplier"
            >
              Cancel
            </v-btn>
            <v-btn
              dark
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="AddSupplier"
            >
              <v-icon left small>mdi-check</v-icon>
              Save Supplier
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Modal 2: Edit Supplier Dialog -->
    <v-dialog max-width="800px" v-model="editdialogue" scrollable persistent content-class="admin-supplier-dialog">
      <v-card class="modal-card rounded-lg overflow-hidden d-flex flex-column" style="max-height: 85vh;" v-if="curItem">
        <div class="modal-header d-flex align-center justify-space-between px-6 py-4" style="flex-shrink: 0;">
          <div class="d-flex align-center">
            <v-icon color="#9a6b27" class="mr-2">mdi-pencil-box-outline</v-icon>
            <div>
              <div class="modal-title">Edit Supplier Profile</div>
              <div class="modal-subtitle">Update vendor contact, address, or statutory information</div>
            </div>
          </div>
          <v-btn icon small @click="closeEditSupplier">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-form v-model="editcat" ref="editcat" @submit.prevent class="d-flex flex-column overflow-hidden flex-grow-1">
          <div class="modal-body px-6 py-4 overflow-y-auto flex-grow-1" style="max-height: calc(85vh - 130px);">
            <!-- Section 1: Basic Information -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-card-account-details-outline</v-icon>
              <span>1. General Supplier Information</span>
            </div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <label class="field-label">Supplier Name <span class="required-star">*</span></label>
                <v-text-field
                  :rules="[rules.required]"
                  placeholder="Supplier Name"
                  v-model="curItem.supplierName"
                  outlined
                  dense
                  color="#9a6b27"
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Mobile Number</label>
                <v-text-field
                  placeholder="Contact Number"
                  v-model="curItem.contactNumber"
                  outlined
                  color="#9a6b27"
                  dense
                  type="number"
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Email Address</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Email"
                  v-model="curItem.email"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">City / Location</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Location"
                  v-model="curItem.supplierLocation"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <label class="field-label">Supplier Address</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Address"
                  v-model="curItem.address"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4" style="border-color: #f1f5f9;"></v-divider>

            <!-- Section 2: Statutory & Tax Credentials -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-shield-check-outline</v-icon>
              <span>2. Statutory &amp; Regulatory Credentials</span>
            </div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="4">
                <label class="field-label">GSTIN</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="GSTIN"
                  v-model="curItem.GSTIN"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">TAN</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="TAN"
                  v-model="curItem.TAN"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">BIS License</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="BIS License"
                  v-model="curItem.BisLicense"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Policy No</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Policy No"
                  v-model="curItem.policyNo"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Company ID</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Company ID"
                  v-model="curItem.companyId"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4" style="border-color: #f1f5f9;"></v-divider>

            <!-- Section 3: Banking & Payment Settlement -->
            <div class="form-section-title d-flex align-center mb-3">
              <v-icon small color="#9a6b27" class="mr-1">mdi-bank-outline</v-icon>
              <span>3. Banking &amp; Settlement Account</span>
            </div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="field-label">Bank Name</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="Bank Name"
                  v-model="curItem.bankName"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Account No</label>
                <v-text-field
                  color="#9a6b27"
                  type="number"
                  placeholder="Account Number"
                  v-model="curItem.accountNo"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">IFSC Code</label>
                <v-text-field
                  color="#9a6b27"
                  placeholder="IFSC Code"
                  v-model="curItem.IFSCCode"
                  outlined
                  dense
                  class="pro-input"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <div class="modal-footer d-flex align-center justify-end px-6 py-3 border-t bg-white" style="flex-shrink: 0; background: #ffffff; border-top: 1px solid #e2e8f0;">
            <v-btn
              text
              class="btn-cancel text-capitalize mr-2"
              @click="closeEditSupplier"
            >
              Cancel
            </v-btn>
            <v-btn
              dark
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="editSupplier"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Save Changes
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Modal 3: Delete Confirmation Dialog -->
    <v-dialog width="420px" v-model="deletedialog" content-class="admin-supplier-dialog">
      <v-card class="modal-card rounded-lg overflow-hidden pa-4">
        <div class="text-center py-3">
          <div class="delete-icon-box mx-auto mb-3">
            <v-icon size="32" color="#dc2626">mdi-trash-can-outline</v-icon>
          </div>
          <div class="delete-title mb-1">Delete Supplier</div>
          <div class="delete-subtitle px-4">
            Are you sure you want to delete this supplier? This action cannot be undone if there are active transactions.
          </div>
        </div>

        <div class="d-flex align-center justify-center mt-4" style="gap: 12px;">
          <v-btn
            outlined
            color="#64748b"
            class="text-capitalize"
            style="border-radius: 8px;"
            @click="deletedialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            dark
            color="#dc2626"
            class="text-capitalize elevation-1"
            style="border-radius: 8px;"
            @click="deleteSupplier"
          >
            Confirm Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,
      addcat: false,
      editcat: false,
      jewelleryId: null,
      rolesList: [],
      supplierName: null,
      email: null,
      contact: null,
      address: null,
      location: null,
      gstin: null,
      tan: null,
      bisliscence: null,
      policyno: null,
      companyid: null,
      bankname: null,
      accno: null,
      ifsc: null,
      branch: null,
      pan: null,
      keyword: null,
      addSupplier: false,
      supplierList: [],
      curItem: {},
      curId: null,
      editdialogue: false,
      deletedialog: false,

      rules: {
        required: (value) => !!value || "This field is required",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
        email: (value) => {
          if (!value) return true;
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email address";
        },
      },
      phoneRules: [
        (v) => !v || (v.length >= 10 && v.length <= 15) || "Invalid phone number",
      ],
      panRules: [
        (value) => !value || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value) || "Invalid PAN number format",
      ],
    };
  },
  mounted() {
    this.getSupplier();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalSuppliersCount() {
      return this.totalCount || (this.supplierList ? this.supplierList.length : 0);
    },
    totalPurchasedGoldSum() {
      if (!this.supplierList || !this.supplierList.length) return "0.00";
      const sum = this.supplierList.reduce((acc, item) => acc + (Number(item.totalPurchasedGold) || 0), 0);
      return sum.toFixed(2);
    },
    totalBalanceThankamSum() {
      if (!this.supplierList || !this.supplierList.length) return "0.00";
      const sum = this.supplierList.reduce((acc, item) => acc + (Number(item.balanceThankam) || 0), 0);
      return sum.toFixed(2);
    },
    totalPendingGoldSum() {
      if (!this.supplierList || !this.supplierList.length) return "0.00";
      const sum = this.supplierList.reduce((acc, item) => acc + (Number(item.totalPendingGold) || 0), 0);
      return sum.toFixed(2);
    },
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getSupplier();
    },
    page() {
      this.getSupplier();
    },
  },
  methods: {
    formatWeight(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toFixed(2);
    },
    getInitials(name) {
      if (!name) return "S";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    openAddSupplier() {
      this.resetAddForm();
      this.addSupplier = true;
    },
    closeAddSupplier() {
      this.addSupplier = false;
      this.resetAddForm();
    },
    resetAddForm() {
      if (this.$refs.addcat) {
        this.$refs.addcat.resetValidation();
      }
      this.supplierName = "";
      this.email = null;
      this.contact = null;
      this.address = null;
      this.location = null;
      this.gstin = null;
      this.tan = null;
      this.bisliscence = null;
      this.policyno = null;
      this.companyid = null;
      this.bankname = null;
      this.accno = null;
      this.ifsc = null;
      this.branch = null;
      this.pan = null;
    },
    openEditSupplier(item) {
      this.curItem = { ...item };
      this.editdialogue = true;
    },
    closeEditSupplier() {
      this.editdialogue = false;
      if (this.$refs.editcat) {
        this.$refs.editcat.resetValidation();
      }
    },
    openDeleteSupplier(id) {
      this.curId = id;
      this.deletedialog = true;
    },
    getSupplier() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/supplier/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword || "",
          page: this.page,
          count: 20,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status === true) {
            this.supplierList = response.data.data || [];
            this.totalCount = response.data.count || this.supplierList.length;
            this.Pagelength = Math.ceil((response.data.count || this.supplierList.length) / 20) || 1;
          } else {
            this.supplierList = [];
            this.showsnackbar = true;
            this.msg = (response.data && response.data.msg) || "Unable to fetch suppliers";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    AddSupplier() {
      if (this.$refs.addcat.validate()) {
        this.appLoading = true;
        axios({
          url: "/supplier/add",
          method: "POST",
          data: {
            supplierName: this.supplierName,
            email: this.email,
            contactNumber: this.contact,
            address: this.address,
            supplierLocation: this.location,
            GSTIN: this.gstin,
            TAN: this.tan,
            BisLicense: this.bisliscence,
            policyNo: this.policyno,
            companyId: this.companyid,
            bankName: this.bankname,
            accountNo: this.accno,
            IFSCCode: this.ifsc,
            branch: this.branch,
            pancardNo: this.pan,
          },
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status === true) {
              this.msg = response.data.msg || "Supplier created successfully";
              this.showsnackbar = true;
              this.addSupplier = false;
              this.resetAddForm();
              this.getSupplier();
            } else {
              this.msg = response.data.msg || "Error creating supplier";
              this.showsnackbar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.error(err);
          });
      }
    },
    editSupplier() {
      if (this.$refs.editcat.validate()) {
        this.appLoading = true;
        axios({
          url: "/supplier/edit",
          method: "POST",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: {
            id: this.curItem._id,
            contactNumber: this.curItem.contactNumber,
            supplierName: this.curItem.supplierName,
            supplierLocation: this.curItem.supplierLocation,
            address: this.curItem.address,
            email: this.curItem.email,
            GSTIN: this.curItem.GSTIN,
            TAN: this.curItem.TAN,
            BisLicense: this.curItem.BisLicense,
            policyNo: this.curItem.policyNo,
            companyId: this.curItem.companyId,
            bankName: this.curItem.bankName,
            accountNo: this.curItem.accountNo,
            IFSCCode: this.curItem.IFSCCode,
          },
        })
          .then((response) => {
            this.appLoading = false;
            this.editdialogue = false;
            if (response.data.status === true) {
              this.msg = response.data.msg || "Supplier updated successfully";
              this.showsnackbar = true;
              this.getSupplier();
            } else {
              this.msg = response.data.msg || "Error updating supplier";
              this.showsnackbar = true;
              this.getSupplier();
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.error(err);
          });
      }
    },
    deleteSupplier() {
      this.appLoading = true;
      axios({
        url: "/supplier/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Supplier deleted";
            this.showsnackbar = true;
            this.getSupplier();
          } else {
            this.msg = response.data.msg || "Unable to delete supplier";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.supplier-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.search-input-wrap {
  width: 320px;
}

@media (max-width: 960px) {
  .search-input-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.supplier-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 700;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-name-text {
  font-weight: 600;
  color: #0f172a;
  font-size: 13.5px;
}

.supplier-location-text {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.code-pill {
  font-family: monospace;
  font-weight: 600;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  color: #1e293b;
}

.action-icon-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  width: 30px !important;
  height: 30px !important;
}

.action-icon-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

.action-icon-btn.delete-btn:hover {
  background: #fef2f2 !important;
  border-color: #fca5a5 !important;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.text-muted {
  color: #94a3b8;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

/* Modal Dialogs */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.form-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.required-star {
  color: #dc2626;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
}

.btn-cancel {
  border-radius: 8px !important;
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.delete-icon-box {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-subtitle {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
}

/* Offset and center modals in the workspace area away from the left sidebar */
@media (min-width: 1264px) {
  .admin-supplier-dialog {
    margin-left: calc(100vw / 12) !important;
    margin-right: auto !important;
  }
}
</style>
